import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type FilingStatus =
  | "Single"
  | "MarriedFilingJointly"
  | "MarriedFilingSeparately"
  | "HeadOfHousehold"
  | "QualifyingSurvivingSpouse";

export type EntityType =
  | "SoleProprietor"
  | "SingleMemberLLC"
  | "MultiMemberLLC"
  | "SCorp"
  | "CCorp"
  | "Partnership";

export type TaxTreatment = "DisregardedScheduleC" | "SCorp" | "CCorp" | "Partnership";

export type BoiStatus =
  | "NotFiled"
  | "Filed"
  | "Exempt"
  | "Updated"
  | "HandledByAgent"
  | "NotRequired";

export interface BusinessProfile {
  id: string;
  ownerUserId: string;
  legalName: string;
  doingBusinessAs?: string | null;
  ein?: string | null;
  entityType: EntityType;
  taxTreatment: TaxTreatment;
  formationDate?: string | null;
  formationState?: string | null;
  naics?: string | null;
  fiscalYearStartMonth: number;
  registeredAgentName?: string | null;
  registeredAgentRenewalDate?: string | null;
  stateOfOperation?: string | null;
  boiStatus: BoiStatus;
  boiConfirmationNumber?: string | null;
  boiFiledOn?: string | null;
  sCorpEffectiveDate?: string | null;
  sCorpForm2553FiledOn?: string | null;
}

export interface DependentChild {
  id: string;
  ownerUserId: string;
  familyProfileId: string;
  firstName: string;
  lastName?: string | null;
  dateOfBirth: string;
  isOnPayroll: boolean;
  payrollStartDate?: string | null;
  roleDescription?: string | null;
  custodialRothIraProvider?: string | null;
  custodialRothIraYtdContribution?: number | null;
  edvest529AccountNumberMasked?: string | null;
  edvest529YtdContribution?: number | null;
}

export interface FamilyProfile {
  id: string;
  ownerUserId: string;
  /** Nullable: until the user picks one, the engine cannot project. */
  filingStatus: FilingStatus | null;
  ownerFirstName?: string | null;
  ownerLastName?: string | null;
  ownerDateOfBirth?: string | null;
  hasSpouse: boolean;
  spouseFirstName?: string | null;
  spouseLastName?: string | null;
  spouseDateOfBirth?: string | null;
  /** Nullable: required to compute state tax. */
  stateOfResidence: string | null;
  priorYearTotalTax?: number | null;
  priorYearAgi?: number | null;
  isHsaEligible: boolean;
  /**
   * Three-state answer to the intake question "Have you formed a business entity?".
   * - `null` = never asked → Setup Center stays in the Intake stage
   * - `false` = user said "no" → business-gated checklist items suppressed
   * - `true` = user said "yes" → a BusinessProfile row is expected
   */
  hasBusinessEntity?: boolean | null;
  /**
   * Set when the user finishes (or explicitly skips) the conversational intake
   * wizard. The route guard in AppShell uses this to decide whether to force
   * the wizard on first load. Null = wizard not yet completed.
   */
  onboardingCompletedAt?: string | null;
  /**
   * Pain-point picker answer captured at the very top of the wizard. Short
   * token: "tax" | "debt" | "retirement" | "setup" | "other". Drives the
   * dashboard ranking. Null until answered.
   */
  focusArea?: string | null;
  children: DependentChild[];
}

const BUSINESS_KEY = ["profile", "business"];
const FAMILY_KEY = ["profile", "family"];

export function useBusinessProfile() {
  return useQuery({
    queryKey: BUSINESS_KEY,
    queryFn: async (): Promise<BusinessProfile> => {
      const r = await api().get<BusinessProfile>("/api/business-profile");
      return r.data;
    },
  });
}

export function useUpdateBusinessProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<BusinessProfile>) => {
      const r = await api().put<BusinessProfile>("/api/business-profile", input);
      return r.data;
    },
    // Prime the cache with the server-returned record IMMEDIATELY, before
    // invalidation kicks off the background refetch. Consumers (the intake
    // wizard in particular) read `business.data` and spread it into the
    // NEXT save — if that read still saw the stale pre-save value, the
    // next PUT would overwrite whatever field we just set back to null.
    onSuccess: (saved) => {
      qc.setQueryData(BUSINESS_KEY, saved);
      qc.invalidateQueries({ queryKey: BUSINESS_KEY });
      qc.invalidateQueries({ queryKey: ["advisor", "cards"] });
      qc.invalidateQueries({ queryKey: ["strategy", "cash-allocation"] });
      qc.invalidateQueries({ queryKey: ["strategy", "entity-comparison"] });
      qc.invalidateQueries({ queryKey: ["tax", "projection"] });
      qc.invalidateQueries({ queryKey: ["setup-checklist"] });
    },
  });
}

/**
 * Quick action: set BOI status from anywhere in the app (Dashboard advisor card,
 * People page, Setup Center). Lets the user dismiss the BOI alert in one click
 * once they confirm their registered agent handles it / it isn't required.
 */
export function useSetBoiStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      status: BoiStatus;
      filedOn?: string | null;
      confirmationNumber?: string | null;
    }) => {
      const r = await api().post<BusinessProfile>(
        "/api/business-profile/boi-status",
        input,
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BUSINESS_KEY });
      qc.invalidateQueries({ queryKey: ["advisor", "cards"] });
    },
  });
}

export function useFamilyProfile() {
  return useQuery({
    queryKey: FAMILY_KEY,
    queryFn: async (): Promise<FamilyProfile> => {
      const r = await api().get<FamilyProfile>("/api/family-profile", {
        timeout: 5000,
      });
      return r.data;
    },
    retry: 1,
    retryDelay: 1000,
  });
}

export function useUpdateFamilyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<FamilyProfile>) => {
      const r = await api().put<FamilyProfile>("/api/family-profile", input);
      return r.data;
    },
    // Same rationale as useUpdateBusinessProfile: the intake wizard saves
    // one field at a time by spreading the current `family.data` and
    // overriding a single property. If the NEXT save happens before the
    // invalidation-triggered refetch lands, the spread would read the
    // pre-save snapshot and the previous answer would silently regress to
    // null. Writing the server response directly into the cache eliminates
    // that window.
    onSuccess: (saved) => {
      qc.setQueryData(FAMILY_KEY, saved);
      qc.invalidateQueries({ queryKey: FAMILY_KEY });
      qc.invalidateQueries({ queryKey: ["advisor", "cards"] });
      qc.invalidateQueries({ queryKey: ["strategy", "cash-allocation"] });
      qc.invalidateQueries({ queryKey: ["strategy", "entity-comparison"] });
      qc.invalidateQueries({ queryKey: ["tax", "projection"] });
      qc.invalidateQueries({ queryKey: ["setup-checklist"] });
    },
  });
}
